import { ApiRoutes } from "@/lib/routeResolver";
import { cache } from "react";
import api from "./api";

// ─── Type Definitions ────────────────────────────────────────────────

export interface MediaCoverageLink {
  title: string;
  externalLink: string;
}

export interface PressRelease {
  id: string;
  title: string;
  description: string;
  image: string;
  pageUrl: string;
  isActive: boolean;
  isExternalLink: boolean;
  externalLink: string;
  order: number;
  createdDate: string;
  companyId: string;
  destinationId?: string;
  destinationName?: string;
  mediaCoverageObj?: MediaCoverageLink[];
}

interface PressReleaseApiResponse {
  pageNumber: number;
  pageSize: number;
  count: number;
  totalPages: number;
  succeeded: boolean;
  data: PressRelease[];
}

// ─── Helpers ─────────────────────────────────────────────────────────

export { stripHtml, truncate } from "./helpers";

export function formatPressDate(dateStr: string): string {
  if (!dateStr) return "";
  return new Date(dateStr).toLocaleDateString("en-US", {
    month: "long",
    day: "numeric",
    year: "numeric",
  });
}

export function formatPressDateShort(dateStr: string) {
  if (!dateStr) return { month: "", day: 0, year: 0 };
  const date = new Date(dateStr);
  return {
    month: date.toLocaleString("en", { month: "short" }),
    day: date.getDate(),
    year: date.getFullYear(),
  };
}

// ─── Server-side Data Fetching ───────────────────────────────────────

export const fetchPressReleases = cache(
  async (
    page = 1,
    pageSize = 8,
    destinationId?: string,
  ): Promise<{
    items: PressRelease[];
    totalPages: number;
    count: number;
  }> => {
    const res = await api.get<PressReleaseApiResponse>(ApiRoutes.WEBSITE_PRESSRELEASE, {
      params: {
        PageNumber: page,
        PageSize: pageSize,
        ...(destinationId ? { DestinationId: destinationId } : {}),
      },
    });

    const items = (res.data?.data ?? []).filter((p) => p.isActive);
    return {
      items,
      totalPages: res.data?.totalPages ?? 1,
      count: res.data?.count ?? 0,
    };
  },
);

export const fetchPressReleaseByPageUrl = cache(
  async (pageUrl: string): Promise<PressRelease | null> => {
    const res = await api.get<PressReleaseApiResponse>(ApiRoutes.WEBSITE_PRESSRELEASE, {
      params: { PageNumber: 1, PageSize: 1, PageUrl: pageUrl },
    });
    const items = (res.data?.data ?? []).filter((p) => p.isActive);
    return items[0] ?? null;
  },
);

// ─── Client-safe fetch (uses NEXT_PUBLIC_ env vars) ─────────────────

const BASE_URL = (process.env.NEXT_PUBLIC_API_URL || "").replace(/\/+$/, "");
const TOKEN = process.env.NEXT_PUBLIC_ACCESS_TOKEN!;
const COMPANY_ID = process.env.NEXT_PUBLIC_COMPANY_ID!;

export async function fetchPressReleasesClient(
  page = 1,
  pageSize = 8,
  destinationId?: string,
): Promise<{
  items: PressRelease[];
  totalPages: number;
  count: number;
}> {
  const params = new URLSearchParams({
    PageNumber: String(page),
    PageSize: String(pageSize),
    CompanyId: COMPANY_ID,
  });
  if (destinationId) params.set("DestinationId", destinationId);

  const res = await fetch(`${BASE_URL}/website/pressrelease?${params}`, {
    headers: {
      "Content-Type": "application/json",
      Authorization: `Bearer ${TOKEN}`,
    },
  });
  const json = await res.json();
  const items: PressRelease[] = (json.data ?? []).filter(
    (p: PressRelease) => p.isActive,
  );
  return {
    items,
    totalPages: json.totalPages ?? 1,
    count: json.count ?? 0,
  };
}
